import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { Plus, Trash2, TrendingUp, TrendingDown, Euro, Sparkles } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { todayLocalIso } from "../../lib/dateUtils";
import { groupByCategory, EXPENSE_DRILL_KINDS } from "../../lib/expenseGrouping";

// 22 categorias granulares agrupadas em 6 famílias — o admin escolhe a
// categoria certa e o donut de dashboard agrega automaticamente por família.
const CATEGORY_LABELS = {
  // Operacionais
  cleaning_products:   "Produtos de limpeza",
  cleaning_material:   "Material de limpeza",
  guest_consumables:   "Consumíveis para clientes",
  laundry:             "Lavandaria",
  equipment:           "Equipamento e ferramentas",
  // Veículos
  fuel:                "Combustível",
  vehicle_maintenance: "Manutenção de veículos",
  tolls_parking:       "Portagens e estacionamento",
  vehicle_insurance:   "Seguros de veículos",
  // Pessoal
  payroll:             "Salários e ordenados",
  social_security:     "Segurança Social",
  training:            "Formação da equipa",
  uniforms_ppe:        "Fardas / EPI",
  // Estrutura
  rent:                "Renda / arrendamento",
  utilities:           "Eletricidade, água, comunicações",
  software:            "Software e subscrições",
  accounting:          "Contabilidade / serviços profissionais",
  marketing:           "Marketing e publicidade",
  banking:             "Bancos e comissões",
  // Impostos e outros
  tax:                 "IVA e impostos",
  maintenance:         "Manutenção geral",
  other:               "Outro / miscelâneas",
  // Buckets auto-gerados noutros fluxos
  supplies:            "Material de limpeza",   // legacy alias
  produtos:            "Produtos (Facturalusa)",
  materiais:           "Materiais (Facturalusa)",
  stripe_fees:         "Comissões Stripe",
};

// Grupos usados no <optgroup> do formulário — mantém a ordem de exibição.
const CATEGORY_GROUPS = [
  { label: "Operacionais (limpeza no local)", keys: ["cleaning_products", "cleaning_material", "guest_consumables", "laundry", "equipment"] },
  { label: "Veículos e deslocações", keys: ["fuel", "vehicle_maintenance", "tolls_parking", "vehicle_insurance"] },
  { label: "Pessoal", keys: ["payroll", "social_security", "training", "uniforms_ppe"] },
  { label: "Estrutura e escritório", keys: ["rent", "utilities", "software", "accounting", "marketing", "banking"] },
  { label: "Impostos e outros", keys: ["tax", "maintenance", "other"] },
];

// 6 famílias — usadas pelo donut chart de despesas.
const FAMILY_LABELS = {
  operacionais: "Operacionais",
  veiculos:     "Veículos",
  pessoal:      "Pessoal",
  estrutura:    "Estrutura",
  impostos:     "Impostos",
  outros:       "Outros",
};
const PIE_COLORS = ["#d4af37", "#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#a0b2af"];

// ChartBox — defers mounting the ResponsiveContainer until the parent box has
// measured > 0 dimensions. Prevents Recharts' "width(-1) height(-1)" warnings
// during first paint (container still in layout phase).
function ChartBox({ height = 260, children }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) setReady(true);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: "100%", height, minWidth: 0 }}>
      {ready ? (
        <ResponsiveContainer width="100%" height="100%" debounce={50}>{children}</ResponsiveContainer>
      ) : null}
    </div>
  );
}

const TOOLTIP_STYLE = {
  background: "#062c27",
  border: "1px solid #1a4640",
  borderRadius: 2,
  color: "#fdfbf7",
  fontFamily: "Outfit, sans-serif",
};

/**
 * AdminFinance — monthly revenue vs expenses chart, cleanings per month,
 * expense distribution donut, plus a form to log new expenses.
 */
export default function AdminFinance() {
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [months, setMonths] = useState(12);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    description: "",
    amount: "",
    category: "cleaning_material",
    date: todayLocalIso(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [drillDown, setDrillDown] = useState(null);

  const load = useCallback(async () => {
    try {
      const [dash, exp] = await Promise.all([
        api.get(`/admin/finance/dashboard?months=${months}`),
        api.get("/admin/expenses"),
      ]);
      setData(dash.data);
      setExpenses(exp.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  async function addExpense(e) {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) || amount < 0) { toast.error("Valor inválido."); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/expenses", { ...form, amount });
      toast.success("Despesa adicionada.");
      setForm(f => ({ ...f, description: "", amount: "" }));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  }

  async function removeExpense(id) {
    if (!window.confirm("Eliminar esta despesa?")) return;
    try { await api.delete(`/admin/expenses/${id}`); toast.success("Eliminada."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  if (loading || !data) return <div className="text-[#a0b2af]">A carregar balanço…</div>;

  const totals = data.totals;
  const positive = totals.balance >= 0;

  // Snapshot do mês corrente — dia 1 até último dia (live). O backend já
  // devolve `current_month` com a quebra completa (receitas, despesas
  // operacionais, produtos, materiais, etc.). Cai para a última entrada
  // da série em backends mais antigos.
  const fallback = (data.series || [])[data.series.length - 1] || {
    month: "—", revenue: 0, expenses: 0, balance: 0, upcoming: 0, cleanings: 0,
    breakdown: {},
  };
  const currentMonth = data.current_month || { ...fallback, breakdown: fallback.breakdown || {} };
  const breakdown = currentMonth.breakdown || {};
  // Mês anterior, para mostrar variação percentual quando existe.
  const previousMonth = (data.series || [])[data.series.length - 2] || null;
  const monthLabel = (() => {
    const [y, m] = (currentMonth.month || "").split("-");
    if (!y || !m) return "Mês corrente";
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
  })();
  const todayInfo = (() => {
    const d = new Date();
    const day = d.getDate();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return { day, last };
  })();
  function pct(curr, prev) {
    if (!prev || prev === 0) return null;
    return ((curr - prev) / Math.abs(prev)) * 100;
  }
  const revDelta = pct(currentMonth.revenue, previousMonth?.revenue);
  const expDelta = pct(currentMonth.expenses, previousMonth?.expenses);

  return (
    <div className="space-y-10" data-testid="finance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Balanço</div>
          <h1 className="font-display text-5xl">Finanças.</h1>
          <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
            Receita gerada pelas limpezas concluídas menos as suas despesas operacionais — numa vista mensal.
          </div>
        </div>
        <div className="flex gap-2" data-testid="finance-period">
          {[6, 12, 24].map(m => (
            <button key={m} onClick={() => setMonths(m)}
                    className={`px-3 py-2 text-xs uppercase tracking-widest border rounded-sm ${months === m ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]" : "border-[#1a4640] text-[#a0b2af]"}`}
                    data-testid={`period-${m}`}>
              {m} meses
            </button>
          ))}
        </div>
      </div>

      {/* TOTALS STRIP — clickable cards open detail drill-down */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Receita" value={`${totals.revenue.toLocaleString("pt-PT")}€`} icon={<TrendingUp size={14} />} accent="gold"
                  onClick={() => setDrillDown("revenue")} testId="stat-revenue-click" />
        <StatCard label="Despesas" value={`${totals.expenses.toLocaleString("pt-PT")}€`} icon={<TrendingDown size={14} />} accent="red"
                  onClick={() => setDrillDown("expenses")} testId="stat-expenses-click" />
        <StatCard label="Balanço" value={`${totals.balance.toLocaleString("pt-PT")}€`} icon={<Euro size={14} />} accent={positive ? "green" : "red"}
                  onClick={() => setDrillDown("balance")} testId="stat-balance-click" />
        <StatCard label="Limpezas" value={totals.cleanings} icon={<Sparkles size={14} />}
                  onClick={() => setDrillDown("cleanings")} testId="stat-cleanings-click" />
      </div>

      {/* MÊS CORRENTE — destaque do período em curso (dia 1 → último) */}
      <section className="lux-card p-6 border-l-4 border-[#d4af37]
                          bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.08),transparent_55%)]"
               data-testid="finance-current-month">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
          <div>
            <div className="eyebrow text-[#d4af37]">Mês corrente</div>
            <h2 className="font-display text-3xl mt-1 capitalize">{monthLabel}</h2>
            <div className="text-[11px] text-[#a0b2af] mt-1">
              Contabilizado do dia 1 ao dia {todayInfo.last} · hoje é dia {todayInfo.day}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0b2af]">Saldo do mês</div>
            <div className={`font-display text-4xl ${currentMonth.balance >= 0 ? "text-[#34d399]" : "text-[#f87171]"}`}
                 data-testid="current-month-balance">
              {Number(currentMonth.balance).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MonthBox label="Receita" testId="current-month-revenue"
                    value={currentMonth.revenue} delta={revDelta} positiveIsGood
                    accent="gold" />
          <MonthBox label="Despesas totais" testId="current-month-expenses"
                    value={currentMonth.expenses} delta={expDelta} positiveIsGood={false}
                    accent="red" />
          <MonthBox label="Limpezas" testId="current-month-cleanings"
                    value={currentMonth.cleanings} delta={null} positiveIsGood
                    accent="gold" suffix="" raw />
          <MonthBox label="Pendente" testId="current-month-upcoming"
                    value={currentMonth.upcoming} delta={null} positiveIsGood={false}
                    accent="amber" />
        </div>

        {/* Quebra das despesas do mês — origem por origem para ajudar o
            admin a perceber porque o saldo do mês ficou no valor mostrado. */}
        {currentMonth.expenses > 0 && (
          <div className="mt-5 p-4 bg-[#031c18] border border-[#1a4640]"
               data-testid="current-month-breakdown">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0b2af] mb-3">
              Origem das despesas deste mês
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <BreakdownBox label="Operacionais" value={breakdown.despesas || 0}
                            testId="break-despesas" />
              <BreakdownBox label="Produtos" value={breakdown.produtos || 0}
                            testId="break-produtos" />
              <BreakdownBox label="Materiais" value={breakdown.materiais || 0}
                            testId="break-materiais" />
              <BreakdownBox label="Taxas Stripe" value={breakdown.stripe_fees || 0}
                            testId="break-stripe" />
              <BreakdownBox label="Outros" value={breakdown.outros || 0}
                            testId="break-outros" />
            </div>
            <div className="mt-3 text-[11px] text-[#a0b2af]">
              <strong className="text-[#fcd34d]">Saldo do mês</strong> = Receitas <span className="text-[#34d399]">({Number(currentMonth.revenue).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €)</span>{" "}
              − Despesas totais <span className="text-[#f87171]">({Number(currentMonth.expenses).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €)</span>{" "}
              = <strong className={currentMonth.balance >= 0 ? "text-[#34d399]" : "text-[#f87171]"}>
                {Number(currentMonth.balance).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €
              </strong>
            </div>
          </div>
        )}
      </section>

      {/* Pagamentos por vir — pipeline de cash-in pendente, não contabilizado nas receitas. */}
      {(totals.upcoming || 0) > 0 && (
        <div className="lux-card p-5 border border-[#1a4640]
                        bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.08),transparent_55%)]"
             data-testid="finance-upcoming-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0b2af] mb-1">
                Pagamentos por vir · não contabilizado no balanço
              </div>
              <div className="font-display text-3xl text-[#fcd34d] break-words"
                   data-testid="finance-upcoming-value">
                {Number(totals.upcoming).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €
              </div>
              <div className="text-[11px] text-[#a0b2af] mt-1">
                {totals.upcoming_count} {totals.upcoming_count === 1 ? "reserva registada" : "reservas registadas"} ainda por pagar
              </div>
              {/* Split sem/com IVA — útil quando há faturas manuais com IVA */}
              {(totals.upcoming_vat || 0) > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]
                                border-t border-[#1a4640] pt-2"
                     data-testid="finance-upcoming-vat-split">
                  <div className="text-[#a0b2af]">
                    Sem IVA (líquido)
                    <div className="font-mono text-[13px] text-[#d4af37] mt-0.5"
                         data-testid="finance-upcoming-net">
                      {Number(totals.upcoming_net).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €
                    </div>
                  </div>
                  <div className="text-[#a0b2af]">
                    IVA a liquidar
                    <div className="font-mono text-[13px] text-[#fca5a5] mt-0.5"
                         data-testid="finance-upcoming-vat">
                      {Number(totals.upcoming_vat).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="text-[11px] text-[#a0b2af] max-w-sm">
              Estas reservas só entram em receitas/balanço quando confirmar o pagamento na ficha da reserva.
            </div>
          </div>
        </div>
      )}

      {drillDown && (
        <FinanceDrillDown kind={drillDown} months={months} onClose={() => setDrillDown(null)} />
      )}

      {/* REVENUE vs EXPENSES — BAR CHART */}
      <section className="lux-card p-6" data-testid="chart-revenue-expenses">
        <div className="eyebrow mb-4">Receita vs despesas · {months} meses</div>
        <ChartBox height={320}>
            <BarChart data={data.series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
              <XAxis dataKey="month" tick={{ fill: "#a0b2af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#a0b2af", fontSize: 10 }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#062c27" }} />
              <Legend wrapperStyle={{ color: "#fdfbf7", fontSize: 12 }} />
              <Bar dataKey="revenue"  name="Receita"  fill="#d4af37" radius={[2, 2, 0, 0]} />
              <Bar dataKey="expenses" name="Despesas" fill="#fca5a5" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ChartBox>
      </section>

      {/* BALANCE LINE + CLEANINGS */}
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="lux-card p-6" data-testid="chart-balance">
          <div className="eyebrow mb-4">Balanço mensal</div>
          <ChartBox height={260}>
              <LineChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
                <XAxis dataKey="month" tick={{ fill: "#a0b2af", fontSize: 10 }} />
                <YAxis tick={{ fill: "#a0b2af", fontSize: 10 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="balance" name="Balanço" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981", r: 4 }} />
              </LineChart>
            </ChartBox>
        </section>

        <section className="lux-card p-6" data-testid="chart-cleanings">
          <div className="eyebrow mb-4">Limpezas concluídas</div>
          <ChartBox height={260}>
              <BarChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
                <XAxis dataKey="month" tick={{ fill: "#a0b2af", fontSize: 10 }} />
                <YAxis tick={{ fill: "#a0b2af", fontSize: 10 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#062c27" }} />
                <Bar dataKey="cleanings" name="Limpezas" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ChartBox>
        </section>
      </div>

      {/* BY TYPE + BY CATEGORY */}
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="lux-card p-6" data-testid="chart-by-type">
          <div className="eyebrow mb-4">Limpezas por tipologia</div>
          <ChartBox height={260}>
              <BarChart data={data.by_type} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
                <XAxis type="number" tick={{ fill: "#a0b2af", fontSize: 10 }} />
                <YAxis dataKey="type" type="category" tick={{ fill: "#a0b2af", fontSize: 11 }} width={40} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#062c27" }} />
                <Bar dataKey="count" name="Total" fill="#d4af37" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ChartBox>
        </section>

        <section className="lux-card p-6" data-testid="chart-expenses-by-cat">
          <div className="eyebrow mb-4">Despesas por família</div>
          {(() => {
            const familyData = data.expenses_by_family && data.expenses_by_family.length > 0
              ? data.expenses_by_family
              : [];
            if (familyData.length === 0) {
              return <div className="text-sm text-[#a0b2af] py-12 text-center">Sem despesas no período.</div>;
            }
            return (
              <ChartBox height={260}>
                <PieChart>
                  <Pie data={familyData} dataKey="amount" nameKey="family"
                       cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}
                       label={({ family, amount }) => `${FAMILY_LABELS[family] || family} (${amount}€)`}
                       labelLine={{ stroke: "#a0b2af" }}>
                    {familyData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE}
                           formatter={(v, n) => [`${v}€`, FAMILY_LABELS[n] || n]} />
                </PieChart>
              </ChartBox>
            );
          })()}
          {/* Ranking granular por categoria — mostra as top 8 abaixo do donut. */}
          {data.expenses_by_category && data.expenses_by_category.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-[#a0b2af]"
                 data-testid="expenses-category-ranking">
              {data.expenses_by_category.slice(0, 8).map((row) => (
                <div key={row.category} className="flex items-center justify-between border-b border-[#1a4640]/50 py-1">
                  <span className="truncate">{CATEGORY_LABELS[row.category] || row.category}</span>
                  <span className="font-mono text-[#fca5a5]">−{row.amount.toFixed(2)}€</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ADD EXPENSE */}
      <section className="lux-card p-6" data-testid="expense-form-card">
        <div className="eyebrow mb-4">Registar despesa</div>
        <form onSubmit={addExpense} className="grid md:grid-cols-5 gap-3">
          <input type="text" required placeholder="Descrição" value={form.description}
                 onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                 className="md:col-span-2 bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2"
                 data-testid="expense-description" />
          <input type="number" min="0" step="0.01" required placeholder="0,00€" value={form.amount}
                 onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                 className="bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2"
                 data-testid="expense-amount" />
          <select value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2"
                  data-testid="expense-category">
            {CATEGORY_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.keys.map((k) => (
                  <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <input type="date" required value={form.date}
                 onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                 className="bg-[#062c27] border border-[#1a4640] rounded-sm px-3 py-2"
                 data-testid="expense-date" />
          <button type="submit" disabled={submitting}
                  className="md:col-span-5 btn-gold py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="expense-submit">
            <Plus size={14} /> {submitting ? "A adicionar…" : "Adicionar despesa"}
          </button>
        </form>
      </section>

      {/* EXPENSES LIST */}
      <section className="lux-card overflow-hidden" data-testid="expenses-list">
        <div className="p-4 border-b border-[#1a4640] flex items-center justify-between">
          <div className="eyebrow">Histórico de despesas · {expenses.length}</div>
          <div className="text-sm text-[#fca5a5]">
            Total: {expenses.reduce((s, e) => s + e.amount, 0).toFixed(2)}€
          </div>
        </div>
        {expenses.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#a0b2af]">Ainda sem despesas registadas.</div>
        ) : (
          <div className="divide-y divide-[#1a4640]">
            {expenses.map(e => (
              <div key={e.id} className="p-4 flex items-center gap-4 hover:bg-[#0a3a33]/30"
                   data-testid={`expense-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{e.description}</div>
                  <div className="text-[11px] text-[#a0b2af] flex gap-3 mt-1">
                    <span className="text-[#d4af37] uppercase tracking-widest">{CATEGORY_LABELS[e.category] || e.category}</span>
                    <span>{e.date}</span>
                  </div>
                </div>
                <div className="font-mono text-sm text-[#fca5a5]">−{e.amount.toFixed(2)}€</div>
                <button onClick={() => removeExpense(e.id)}
                        className="p-2 text-[#fca5a5] hover:text-[#ef4444]"
                        data-testid={`delete-expense-${e.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, icon, accent = "neutral", onClick, testId }) {
  const map = {
    gold: "text-[#d4af37]",
    green: "text-[#10b981]",
    red: "text-[#fca5a5]",
    neutral: "text-[#fdfbf7]",
  };
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      {...(onClick ? { onClick, type: "button", "data-testid": testId } : {})}
      className={`lux-card p-5 text-left ${onClick ? "hover:border-[#d4af37]/60 hover:-translate-y-0.5 transition-all" : ""}`}
    >
      <div className="eyebrow text-[10px] flex items-center gap-1.5">
        <span className="text-[#d4af37]">{icon}</span>{label}
        {onClick && <span className="ml-auto text-[9px] text-[#d4af37]">ver lista →</span>}
      </div>
      <div className={`font-display text-3xl mt-2 ${map[accent]}`}>{value}</div>
    </Wrapper>
  );
}


function BreakdownBox({ label, value, testId }) {
  const amount = Number(value || 0);
  const muted = amount === 0;
  return (
    <div className="bg-[#062c27] border border-[#1a4640] p-3" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className={`font-mono text-base mt-1 ${muted ? "text-[#56655f]" : "text-[#fca5a5]"}`}>
        {muted ? "—" : `${amount.toLocaleString("pt-PT", { minimumFractionDigits: 2 })} €`}
      </div>
    </div>
  );
}

function MonthBox({ label, value, delta, positiveIsGood = true, accent = "gold", testId, raw = false, suffix = "€" }) {
  const colour = {
    gold: "text-[#d4af37]",
    red: "text-[#fca5a5]",
    amber: "text-[#fcd34d]",
    green: "text-[#34d399]",
  }[accent] || "text-[#fdfbf7]";
  const showDelta = Number.isFinite(delta) && Math.abs(delta) > 0.5;
  // A "good" delta is up for revenue/cleanings, down for expenses/pending.
  const deltaGood = showDelta && (positiveIsGood ? delta >= 0 : delta < 0);
  const formatted = raw
    ? value
    : `${Number(value || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2 })} ${suffix}`;
  return (
    <div className="bg-[#062c27] border border-[#1a4640] p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${colour}`}>{formatted}</div>
      {showDelta && (
        <div className={`text-[10px] mt-1 font-mono ${deltaGood ? "text-[#34d399]" : "text-[#fca5a5]"}`}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}% vs mês anterior
        </div>
      )}
      {!showDelta && (
        <div className="text-[10px] mt-1 text-[#56655f]">—</div>
      )}
    </div>
  );
}


function FinanceDrillDown({ kind, months, onClose }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  // Which category groups are expanded to show the raw item list.
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/finance/drill", { params: { kind, months } });
        if (!alive) return;
        setItems(data.items || []);
        setTitle(data.title || "");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [kind, months]);

  const eur = (v) => `${Number(v || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;

  // Kinds that benefit from category grouping. For everything else we keep
  // the flat list (revenue, cleanings, balance).
  const isExpenseKind = EXPENSE_DRILL_KINDS.has(kind);
  // When kind is 'produtos'/'materiais' the payload's `sub` doesn't repeat
  // the category token — use the drill kind as fallback bucket.
  const fallbackCat = (kind === "produtos" || kind === "materiais") ? kind : "other";
  const groups = isExpenseKind ? groupByCategory(items, fallbackCat) : null;

  const grandTotal = items.reduce((s, it) => s + Number(it.value || 0), 0);

  return (
    <div className="fixed inset-0 z-[60] bg-[#010c0a]/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="finance-drill-overlay">
      <div className="bg-[#041f1b] border border-[#1a4640] max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a4640]">
          <div>
            <div className="eyebrow">Detalhe</div>
            <div className="font-display text-xl mt-1">{title || "A carregar…"}</div>
          </div>
          <button onClick={onClose} className="text-[#a0b2af] hover:text-white" data-testid="finance-drill-close">
            ✕
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-10 text-center text-sm text-[#a0b2af]">A carregar…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#a0b2af]">Sem registos.</div>
          ) : isExpenseKind ? (
            // Grouped view: category totals with expandable raw item list.
            <div className="divide-y divide-[#1a4640]" data-testid="finance-drill-grouped">
              {groups.map((g) => {
                const isOpen = !!openGroups[g.category];
                return (
                  <div key={g.category} data-testid={`drill-group-${g.category}`}>
                    <button
                      onClick={() => setOpenGroups((p) => ({ ...p, [g.category]: !p[g.category] }))}
                      className="w-full text-left px-6 py-3 flex items-center justify-between hover:bg-[#0a3a33]/40"
                      data-testid={`drill-group-toggle-${g.category}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-[#a0b2af] text-xs w-4">{isOpen ? "▾" : "▸"}</span>
                        <div className="min-w-0">
                          <div className="text-sm text-[#fdfbf7] truncate">
                            {CATEGORY_LABELS[g.category] || g.category}
                          </div>
                          <div className="text-[11px] text-[#a0b2af] mt-0.5">
                            {g.items.length} {g.items.length === 1 ? "registo" : "registos"}
                          </div>
                        </div>
                      </div>
                      <div className="font-display text-lg tabular-nums whitespace-nowrap text-[#fca5a5]">
                        −{eur(g.total)}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="bg-[#031815] divide-y divide-[#1a4640]/60"
                           data-testid={`drill-group-items-${g.category}`}>
                        {g.items.map((it, i) => (
                          <div key={i} className="px-10 py-2 flex items-center gap-4 justify-between">
                            <div className="min-w-0">
                              <div className="text-sm text-[#fdfbf7] truncate">{it.label}</div>
                              <div className="text-[11px] text-[#a0b2af] mt-0.5">{it.sub}</div>
                            </div>
                            <div className="font-mono text-sm tabular-nums whitespace-nowrap text-[#fca5a5]">
                              −{eur(it.value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="divide-y divide-[#1a4640]">
              {items.map((it, i) => (
                <div key={i} className="px-6 py-3 flex items-center gap-4 justify-between hover:bg-[#0a3a33]/30">
                  <div className="min-w-0">
                    <div className="text-sm text-[#fdfbf7] truncate">{it.label}</div>
                    <div className="text-[11px] text-[#a0b2af] mt-0.5">{it.sub}</div>
                  </div>
                  <div className={`font-display text-lg tabular-nums whitespace-nowrap ${it.negative ? "text-[#fca5a5]" : "text-[#d4af37]"}`}>
                    {it.value_display || eur(it.value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {!loading && items.length > 0 && (
          <div className="border-t border-[#1a4640] px-6 py-3 flex items-center justify-between bg-[#031815]"
               data-testid="finance-drill-footer">
            <span className="eyebrow">Total · {items.length} registo(s)</span>
            <span className={`font-display text-xl tabular-nums ${isExpenseKind ? "text-[#fca5a5]" : "text-[#d4af37]"}`}>
              {isExpenseKind ? "−" : ""}{eur(grandTotal)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
